import { Box, Button, ButtonGroup } from "@mui/material";
import moment from "moment";
import SelectDatePicker from "../../../../components/dateRange";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AuditTable from "./AuditTable";
import ScratcherAuditTable from "./ScratcherAuditTable";
import { AuditData } from "../../types";
import HeaderTitle from "../../../../components/PageHeader/HeaderTitle";

type AuditReportGroupBy = 'date' | 'audit';

const AUDIT_GROUP_BY_QUERY_KEY = 'groupBy';
const AUDIT_INVENTORY_MOVEMENTS_QUERY_KEY = 'inventoryMovements';

const parseAuditGroupBy = (value: string | null): AuditReportGroupBy => {
    return value === 'audit' ? 'audit' : 'date';
};

const parseInventoryMovements = (value: string | null) => value === 'true';

export default function Audits({ handleAuditDetails, selectedStore }: { handleAuditDetails?: ((data: AuditData) => void) | null, selectedStore: any }) {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const groupBy = parseAuditGroupBy(searchParams.get(AUDIT_GROUP_BY_QUERY_KEY));
    const showInventoryMovements = parseInventoryMovements(
        searchParams.get(AUDIT_INVENTORY_MOVEMENTS_QUERY_KEY)
    );

    const handleGroupByChange = (nextGroupBy: AuditReportGroupBy) => {
        if (nextGroupBy === groupBy) {
            return;
        }

        setSearchParams((currentParams) => {
            const nextParams = new URLSearchParams(currentParams);
            nextParams.set(AUDIT_GROUP_BY_QUERY_KEY, nextGroupBy);
            return nextParams;
        });
    };

    const handleToggleInventoryMovements = () => {
        setSearchParams((currentParams) => {
            const nextParams = new URLSearchParams(currentParams);
            const nextValue = !showInventoryMovements;
            nextParams.set(AUDIT_INVENTORY_MOVEMENTS_QUERY_KEY, nextValue ? 'true' : 'false');
            return nextParams;
        });
    };

    const onAuditDetails = (data: AuditData) => {
        const dateParam = (data as any)?.report_date
            ? `?auditDate=${moment.parseZone((data as any).report_date).format('YYYY-MM-DD')}`
            : (data as any)?.report_datetime
                ? `?auditDate=${moment.parseZone((data as any).report_datetime).format('YYYY-MM-DD')}`
                : '';
        handleAuditDetails ? handleAuditDetails(data) : navigate(`/store/${id}/audits/${data.audit_id}${dateParam}`);
    };

    return (
        <div className="audits-page">
            <Box className="audits-page__header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <HeaderTitle title="Audit Reports" />
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'stretch',
                            border: '1px solid #CBD5E1',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            backgroundColor: '#FFFFFF',
                            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                px: 1.25,
                                color: '#475569',
                                backgroundColor: '#F8FAFC',
                                borderRight: '1px solid #CBD5E1',
                                fontSize: 13,
                                fontWeight: 600,
                                lineHeight: 1,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Group by:
                        </Box>
                        <ButtonGroup
                            size="small"
                            variant="text"
                            aria-label="Group audit reports by"
                            sx={{
                                '& .MuiButton-root': {
                                    minWidth: 62,
                                    px: 1.5,
                                    py: 0.75,
                                    border: 0,
                                    borderRadius: 0,
                                    color: '#334155',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    lineHeight: 1,
                                    textTransform: 'none',
                                },
                                '& .MuiButton-root + .MuiButton-root': {
                                    borderLeft: '1px solid #CBD5E1',
                                },
                                '& .MuiButton-contained': {
                                    backgroundColor: '#2563EB',
                                    color: '#FFFFFF',
                                    boxShadow: 'none',
                                },
                                '& .MuiButton-contained:hover': {
                                    backgroundColor: '#1D4ED8',
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            <Button
                                variant={groupBy === 'date' ? 'contained' : 'outlined'}
                                onClick={() => handleGroupByChange('date')}
                            >
                                By Date
                            </Button>
                            <Button
                                variant={groupBy === 'audit' ? 'contained' : 'outlined'}
                                onClick={() => handleGroupByChange('audit')}
                            >
                                By Shift
                            </Button>
                        </ButtonGroup>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <SelectDatePicker />
                </Box>
            </Box>
            <div className="audits-page__table-section">
                {groupBy === 'audit' ? (
                    <ScratcherAuditTable onAuditDetails={onAuditDetails} selectedStore={selectedStore} />
                ) : (
                    <AuditTable
                        handleAuditDetails={onAuditDetails}
                        selectedStore={selectedStore}
                        showBackstockAccounting={showInventoryMovements}
                        onToggleBackstockAccounting={handleToggleInventoryMovements}
                    />
                )}
            </div>
        </div>
    )
} 
